// Lookups over the static war map (MatchSetupView) for the HUD and the flow screens: cities by CityIndex and by
// SimId, the seats with their capitals and nation names. Built once per war; every lookup of an id the setup did not
// define is a programmer error.

import { COUNTRIES, type Country } from '@taxwar/content'
import { NEUTRAL_OWNER, type CityIndex, type MapCityView, type MatchSetupView, type OwnerValue, type PlayerId, type SimId } from '@taxwar/net'
import { capitalOf, seatsOf } from '../match/warSetup'
import { HudError } from './errors'

const NEUTRAL_NAME = 'Neutral forces'

export class WarMapIndex {
  readonly you: PlayerId
  /** Every seat but yours, ascending: every other belligerent is hostile while no mode has teams. */
  readonly enemies: readonly PlayerId[]
  private readonly byIndex = new Map<number, MapCityView>()
  private readonly bySimId = new Map<number, MapCityView>()
  /** Per seat. */
  private readonly capitals: readonly MapCityView[]
  private readonly nations: readonly Country[]

  constructor(readonly setup: MatchSetupView) {
    this.you = setup.you
    const seats = seatsOf(setup)
    this.enemies = seats.filter((seat) => seat !== setup.you)
    for (const city of setup.cities) {
      this.byIndex.set(city.index, city)
      this.bySimId.set(city.simId, city)
    }
    this.capitals = seats.map((seat) => capitalOf(setup, seat))
    this.nations = setup.belligerents.map((belligerent) => {
      const country = COUNTRIES.get(belligerent.country)
      if (country === undefined) throw new HudError(`war map country ${belligerent.country} is not in the content dataset`)
      return country
    })
  }

  get cities(): readonly MapCityView[] {
    return this.setup.cities
  }

  city(index: CityIndex): MapCityView {
    const city = this.byIndex.get(index)
    if (city === undefined) throw new HudError(`city index ${index} is not on the war map`)
    return city
  }

  cityBySimId(simId: SimId): MapCityView {
    const city = this.bySimId.get(simId)
    if (city === undefined) throw new HudError(`city SimId ${simId} is not on the war map`)
    return city
  }

  /** Capitals are never captured (GDD §5), so the war-start capital is the capital for the whole war. */
  capital(seat: PlayerId): MapCityView {
    const capital = this.capitals[seat]
    if (capital === undefined) throw new HudError(`seat ${seat} is not a belligerent of this war`)
    return capital
  }

  nationName(seat: PlayerId): string {
    const nation = this.nations[seat]
    if (nation === undefined) throw new HudError(`seat ${seat} is not a belligerent of this war`)
    return nation.name
  }

  ownerName(owner: OwnerValue): string {
    return owner === NEUTRAL_OWNER ? NEUTRAL_NAME : this.nationName(owner)
  }
}
